/**
 * Renderer Konva.js pour aperçu des panneaux
 * Affiche visuellement le résultat du formatage
 *
 * Dépendances: Konva.js (chargé via CDN)
 */

// Fix pour le rendu texte Konva (améliore la précision des mesures)
// Référence: GitHub Issue #1852
if (typeof Konva !== 'undefined') {
  Konva._fixTextRendering = true;
}

class PanneauRenderer {
  constructor(containerId) {
    this.containerId = containerId;
    this.stage = null;
    this.layer = null;
    this.container = null;

    // Dimensions physiques réelles (mm)
    this.RECT_WIDTH_MM = 300;
    this.RECT_HEIGHT_MM = 100;
    this.SQUARE_WIDTH_MM = 150;
    this.SQUARE_HEIGHT_MM = 150;
    this.MARGIN_MM = 20;

    // Breakpoints et ratios d'affichage (DOIT être défini AVANT _calculateOptimalRatio)
    this.MOBILE_BREAKPOINT_PX = 750;    // Largeur écran pour mode mobile
    this.MOBILE_MARGIN_PX = 40;         // Marge totale (20px de chaque côté)
    this.MIN_RATIO = 0.5;               // Ratio minimum (qualité) - réduit pour permettre mobile
    this.MAX_RATIO = 1.2;               // Ratio maximum (qualité)
    this.DESKTOP_RATIO = 1.2;           // Ratio standard desktop
    this.MOBILE_SCALE_FACTOR = 0.70;    // Facteur de réduction mobile (30% plus petit)

    // Ratio de conversion mm → pixels pour l'affichage (adaptatif selon taille écran)
    this.MM_TO_PX_RATIO = this._calculateOptimalRatio();

    // Couleurs
    this.COLORS = {
      jaune: '#FDCF4A',
      blanc: '#f3f6f4',
      border: '#191919',
      text: '#191919'
    };

    // Police opentype.js pour conversion texte → path SVG
    this.opentypeFont = null;
    this._opentypeFontLoading = false;
    this._opentypeFontPromise = null;

    // Callbacks à appeler quand la police opentype est chargée
    this._onOpentypeFontLoadedCallbacks = [];

    // Timeouts
    this.SVG_LOAD_TIMEOUT_MS = 5000;  // Timeout pour chargement SVG
    this.FONT_LOAD_TIMEOUT_MS = 1500;   // Timeout chargement police (réduit de 3000 à 1500)
  }

  /**
   * Charge la police Basier Square avec opentype.js pour conversion texte → path
   * Utilise fetch avec ArrayBuffer pour contourner les problèmes CORS
   * Non-bloquant : retourne une Promise et appelle les callbacks enregistrés
   * @private
   * @returns {Promise} Résout quand la police est chargée (ou échoue)
   */
  _loadOpentypeFont() {
    // Si déjà chargée, retourner immédiatement
    if (this.opentypeFont) {
      return Promise.resolve(true);
    }

    // Si déjà en cours de chargement, retourner la promise existante
    if (this._opentypeFontLoading && this._opentypeFontPromise) {
      return this._opentypeFontPromise;
    }

    this._opentypeFontLoading = true;

    this._opentypeFontPromise = new Promise(async (resolve) => {
      try {
        // Attendre que opentype.js soit disponible (max 2 secondes, intervalles courts)
        let waitTime = 0;
        const maxWait = 2000;
        const interval = 50;
        while (typeof opentype === 'undefined' && waitTime < maxWait) {
          await new Promise(r => setTimeout(r, interval));
          waitTime += interval;
        }

        if (typeof opentype === 'undefined') {
          console.warn('[PanneauRenderer] opentype.js not available after 2s');
          resolve(false);
          return;
        }

        const fontUrl = window.PANNEAU_ASSET_URLS?.fontWoff;
        if (!fontUrl) {
          console.warn('[PanneauRenderer] Font WOFF URL not defined');
          resolve(false);
          return;
        }

        // Charger la police via fetch + ArrayBuffer pour éviter les problèmes CORS
        const response = await fetch(fontUrl);
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }
        const arrayBuffer = await response.arrayBuffer();
        this.opentypeFont = opentype.parse(arrayBuffer);
        console.log('[PanneauRenderer] Opentype font loaded successfully');

        // Notifier tous les callbacks enregistrés
        this._onOpentypeFontLoadedCallbacks.forEach(cb => {
          try { cb(); } catch (e) { console.error(e); }
        });
        this._onOpentypeFontLoadedCallbacks = [];

        resolve(true);
      } catch (error) {
        console.error('[PanneauRenderer] Failed to load opentype font:', error);
        this.opentypeFont = null;
        resolve(false);
      } finally {
        this._opentypeFontLoading = false;
      }
    });

    return this._opentypeFontPromise;
  }

  /**
   * Enregistre un callback à appeler quand la police opentype est chargée
   * @param {Function} callback
   */
  onOpentypeFontLoaded(callback) {
    if (this.opentypeFont) {
      // Déjà chargée, appeler immédiatement
      callback();
    } else {
      this._onOpentypeFontLoadedCallbacks.push(callback);
    }
  }

  /**
   * Convertit du texte en path SVG avec opentype.js
   * Retourne la largeur EXACTE du texte en pixels
   * @param {string} text - Le texte à convertir
   * @param {number} fontSizePx - Taille de police en pixels
   * @returns {Object|null} { pathData, width, height } ou null si échec
   * @private
   */
  _textToPath(text, fontSizePx) {
    if (!this.opentypeFont || !text || !text.trim()) {
      return null;
    }

    try {
      // Créer le path avec opentype.js
      const path = this.opentypeFont.getPath(text, 0, 0, fontSizePx);

      // Obtenir la bounding box EXACTE
      const bbox = path.getBoundingBox();

      // Vérifier que la bounding box est valide (pas Infinity)
      if (!isFinite(bbox.x1) || !isFinite(bbox.x2) || !isFinite(bbox.y1) || !isFinite(bbox.y2)) {
        console.warn('[PanneauRenderer] Invalid bounding box for text:', text);
        return null;
      }

      const width = bbox.x2 - bbox.x1;
      const height = bbox.y2 - bbox.y1;

      // Vérifier que les dimensions sont valides
      if (width <= 0 || height <= 0) {
        console.warn('[PanneauRenderer] Invalid dimensions for text:', text);
        return null;
      }

      // Convertir en string SVG path data
      const pathData = path.toPathData(2); // 2 décimales de précision

      return {
        pathData: pathData,
        width: width,
        height: height,
        // Offset pour aligner sur la baseline
        offsetX: bbox.x1,
        offsetY: bbox.y1
      };
    } catch (error) {
      console.error('[PanneauRenderer] Error converting text to path:', error);
      return null;
    }
  }

  /**
   * Crée un Konva.Path à partir de texte avec largeur exacte
   * @param {string} text - Le texte à afficher
   * @param {number} fontSizePx - Taille de police en pixels
   * @param {string} fill - Couleur de remplissage
   * @returns {Object} { node: Konva.Path|Konva.Text, width, height }
   * @private
   */
  _createTextPath(text, fontSizePx, fill = this.COLORS.text) {
    // Vérifier que Konva est disponible
    if (typeof Konva === 'undefined') {
      console.error('[PanneauRenderer] Konva not available');
      return null;
    }

    const pathInfo = this._textToPath(text, fontSizePx);

    if (pathInfo && pathInfo.pathData) {
      // Utiliser Konva.Path pour un contrôle précis
      const textPath = new Konva.Path({
        data: pathInfo.pathData,
        fill: fill,
        // Compensation des offsets de la bounding box
        offsetX: pathInfo.offsetX,
        offsetY: pathInfo.offsetY
      });

      return {
        node: textPath,
        width: pathInfo.width,
        height: pathInfo.height,
        isPath: true
      };
    } else {
      // Fallback sur Konva.Text si opentype.js n'est pas disponible
      const fontFamily = this.basierSquareLoaded
        ? 'Basier Square, Arial, sans-serif'
        : 'Arial, sans-serif';

      const textNode = new Konva.Text({
        text: text,
        fontSize: fontSizePx,
        fontFamily: fontFamily,
        fill: fill,
        padding: 0
      });

      // Log en mode fallback pour debug
      if (!this._fallbackWarned) {
        console.log('[PanneauRenderer] Using Konva.Text fallback (opentype not available)');
        this._fallbackWarned = true;
      }

      return {
        node: textNode,
        width: textNode.width(),
        height: textNode.height(),
        isPath: false
      };
    }
  }

  /**
   * Calcule le ratio optimal MM_TO_PX selon la largeur d'écran
   * @private
   * @returns {number} Ratio adapté
   */
  _calculateOptimalRatio() {
    const screenWidth = window.innerWidth;

    // Sur mobile (≤750px), adapter le ratio pour que le rectangle tienne dans l'écran
    if (screenWidth <= this.MOBILE_BREAKPOINT_PX) {
      // Laisser une marge de sécurité (20px de chaque côté)
      const availableWidth = screenWidth - this.MOBILE_MARGIN_PX;
      // Le panneau rectangle fait 300mm de large
      const ratio = availableWidth / this.RECT_WIDTH_MM;
      // Appliquer le facteur de réduction mobile (15% plus petit)
      const scaledRatio = ratio * this.MOBILE_SCALE_FACTOR;
      // Limiter entre MIN_RATIO et MAX_RATIO pour garder une bonne qualité
      return Math.max(this.MIN_RATIO, Math.min(this.MAX_RATIO, scaledRatio));
    }

    // Desktop : ratio standard 1.2 pour cohérence
    return this.DESKTOP_RATIO;
  }

  /**
   * Vérifie si la police Basier Square est chargée
   * Optimisé pour un chargement rapide
   * @private
   * @returns {Promise<boolean>}
   */
  async _checkFontLoaded() {
    try {
      // Vérifier si l'API FontFace est disponible
      if (!document.fonts) {
        console.warn('[Renderer] FontFace API not available');
        return false;
      }

      // Attendre que toutes les polices soient chargées (timeout court)
      await Promise.race([
        document.fonts.ready,
        new Promise(resolve => setTimeout(resolve, 500)) // Réduit de 3000 à 500ms
      ]);

      // Vérifier immédiatement si Basier Square est disponible
      const fontLoaded = document.fonts.check('16px "Basier Square"');

      if (fontLoaded) {
        // Charger quelques tailles courantes en arrière-plan (non-bloquant)
        const sizes = [16, 24, 32, 40];
        Promise.all(
          sizes.map(size => document.fonts.load(`400 ${size}px "Basier Square"`).catch(() => {}))
        ).catch(() => {});
      }

      return fontLoaded;
    } catch (error) {
      console.error('[Renderer] Error checking font:', error);
      return false;
    }
  }

  /**
   * Initialise le canvas Konva
   * @param {string} dimension - 'rectangle' ou 'carré'
   */
  async init(dimension = 'rectangle') {
    this.container = document.getElementById(this.containerId);
    if (!this.container) {
      console.error(`[Renderer] Conteneur ${this.containerId} introuvable`);
      return false;
    }

    // Démarrer le chargement de la police opentype en parallèle (non-bloquant)
    this._loadOpentypeFont();

    // Vérifier que la police Basier Square est chargée (pour le fallback)
    // Utilise un timeout court pour ne pas bloquer
    this.basierSquareLoaded = await this._checkFontLoaded();

    // Ne PAS attendre la police opentype - on rendra avec fallback si nécessaire
    // Le panneau sera re-rendu automatiquement si opentype charge après

    // Nettoyer le conteneur
    this.container.innerHTML = '';

    // Recalculer le ratio optimal pour la taille actuelle de l'écran
    this.MM_TO_PX_RATIO = this._calculateOptimalRatio();

    // Calculer les dimensions du stage
    const isSquare = dimension === 'carré';
    const widthMm = isSquare ? this.SQUARE_WIDTH_MM : this.RECT_WIDTH_MM;
    const heightMm = isSquare ? this.SQUARE_HEIGHT_MM : this.RECT_HEIGHT_MM;

    const stageWidth = widthMm * this.MM_TO_PX_RATIO;
    const stageHeight = heightMm * this.MM_TO_PX_RATIO;

    // Créer le stage Konva
    this.stage = new Konva.Stage({
      container: this.containerId,
      width: stageWidth,
      height: stageHeight
    });

    // Créer le layer principal
    this.layer = new Konva.Layer();
    this.stage.add(this.layer);

    return true;
  }

  /**
   * Convertit mm en pixels pour l'affichage
   * @param {number} mm - Valeur en mm
   * @returns {number} Valeur en pixels
   */
  mmToPx(mm) {
    return mm * this.MM_TO_PX_RATIO;
  }

  /**
   * Affiche le panneau complet
   * @param {Object} formatResult - Résultat du RhinoFormattingEngine
   * @param {Object} options - { couleur_fond, show_arrow, arrow_position, force_center, show_background_svg }
   */
  render(formatResult, options = {}) {
    const {
      couleur_fond = 'Jaune',
      show_arrow = true,
      arrow_position = 'after',  // 'after' ou 'before'
      arrow_orientation = 0,      // angle en degrés
      force_center = false,       // forcer centrage (pour verso)
      show_background_svg = false // afficher le SVG de fond (pour verso)
    } = options;

    if (!this.layer) {
      console.error('[Renderer] Layer non initialisé');
      return;
    }

    // Nettoyer le layer
    this.layer.destroyChildren();

    const isSquare = formatResult.dimension === 'carré';
    const widthMm = isSquare ? this.SQUARE_WIDTH_MM : this.RECT_WIDTH_MM;
    const heightMm = isSquare ? this.SQUARE_HEIGHT_MM : this.RECT_HEIGHT_MM;

    // Couleur de fond
    const bgColor = couleur_fond.toLowerCase() === 'blanc'
      ? this.COLORS.blanc
      : this.COLORS.jaune;

    // 1. Rectangle de fond
    const background = new Konva.Rect({
      x: 0,
      y: 0,
      width: this.mmToPx(widthMm),
      height: this.mmToPx(heightMm),
      fill: bgColor,
      stroke: this.COLORS.border,
      strokeWidth: 2
    });
    this.layer.add(background);

    // 2.5. SVG de fond (si activé pour le verso)
    if (show_background_svg) {
      this._renderBackgroundSVG(widthMm, heightMm, isSquare);
    }

    // 3. Texte principal (seulement si non vide)
    if (formatResult.title.text && formatResult.title.text.trim()) {
      this._renderTitle(formatResult, widthMm, heightMm, isSquare, arrow_position, arrow_orientation, show_arrow, force_center);

      // 4. Texte secondaire (si existe)
      if (formatResult.secondary.text && formatResult.secondary.text.trim()) {
        this._renderSecondary(formatResult, widthMm, heightMm, isSquare, arrow_position, arrow_orientation, show_arrow, force_center);
      }
    } else if (formatResult.secondary.text && formatResult.secondary.text.trim()) {
      // Texte secondaire seul (sans titre) - utilisé pour le verso
      this._renderSecondaryAlone(formatResult, widthMm, heightMm);
    }

    // 5. Flèche (si format rectangulaire ET show_arrow)
    if (show_arrow && !isSquare) {
      this._renderArrow(widthMm, heightMm, arrow_position, arrow_orientation);
    }

    // Redessiner le layer
    this.layer.batchDraw();
  }

  /**
   * Affiche le texte principal
   * Utilise opentype.js pour conversion texte → path SVG avec largeur exacte
   * @private
   */
  _renderTitle(formatResult, widthMm, heightMm, isSquare, arrowPosition, arrowOrientation, showArrow, forceCenter = false) {
    const centerY = this.mmToPx(heightMm / 2);
    const fontSizePx = this.mmToPx(formatResult.title.height);

    // Buffer de sécurité entre texte et flèche
    const SAFETY_BUFFER_MM = 10;

    // Pour le texte multi-lignes, rendre chaque ligne séparément
    const lines = formatResult.title.text.split('\n');
    const lineHeight = fontSizePx * 1.05; // Interligne réduit
    const totalHeight = lines.length * lineHeight;
    const startY = centerY - totalHeight / 2;

    // Créer un groupe pour contenir toutes les lignes
    const titleGroup = new Konva.Group();

    if (isSquare || forceCenter) {
      // Format carré : centrage parfait avec opentype.js
      const centerXPx = this.mmToPx(widthMm / 2);

      lines.forEach((lineText, index) => {
        if (!lineText.trim()) return;

        const textResult = this._createTextPath(lineText, fontSizePx, this.COLORS.text);
        if (!textResult || !textResult.node) {
          console.error('[PanneauRenderer] Failed to create text for:', lineText);
          return;
        }
        const lineNode = textResult.node;

        // Centrer cette ligne avec la largeur EXACTE
        const lineX = centerXPx - textResult.width / 2;
        const lineY = startY + index * lineHeight;

        lineNode.x(lineX);
        lineNode.y(lineY);

        titleGroup.add(lineNode);
      });

      this.layer.add(titleGroup);

      // Stocker pour le texte secondaire
      this._titleText = {
        y: () => startY,
        height: () => totalHeight,
        offsetY: () => totalHeight
      };
      this._titleGroup = titleGroup;
      return;
    } else {
      // Format rectangle : alignement gauche ou droite selon position de la flèche
      const arrowHorizontalSpace = showArrow ? this._calculateArrowHorizontalSpace(arrowOrientation) : 0;
      const arrowTotalSpaceMm = arrowHorizontalSpace + SAFETY_BUFFER_MM;

      // Zone de texte :
      // - after: texte à gauche, flèche à droite
      // - before: flèche à gauche, texte à droite
      const textZoneStartMm = arrowPosition === 'after' ? this.MARGIN_MM : (this.MARGIN_MM + arrowTotalSpaceMm);
      const textZoneEndMm = arrowPosition === 'after' ? (widthMm - this.MARGIN_MM - arrowTotalSpaceMm) : (widthMm - this.MARGIN_MM);

      lines.forEach((lineText, index) => {
        if (!lineText.trim()) return;

        const textResult = this._createTextPath(lineText, fontSizePx, this.COLORS.text);
        if (!textResult || !textResult.node) {
          console.error('[PanneauRenderer] Failed to create text for:', lineText);
          return;
        }
        const lineNode = textResult.node;

        // Calculer la position X selon l'alignement
        let lineX;
        if (arrowPosition === 'after') {
          // Alignement à gauche
          lineX = this.mmToPx(textZoneStartMm);
        } else {
          // Alignement à droite : position = fin de zone - largeur du texte
          lineX = this.mmToPx(textZoneEndMm) - textResult.width;
        }

        const lineY = startY + index * lineHeight;

        lineNode.x(lineX);
        lineNode.y(lineY);

        titleGroup.add(lineNode);
      });

      this.layer.add(titleGroup);

      // Stocker pour le texte secondaire
      this._titleText = {
        y: () => startY,
        height: () => totalHeight,
        offsetY: () => totalHeight
      };
      this._titleGroup = titleGroup;
    }
  }

  /**
   * Affiche le texte secondaire
   * Utilise opentype.js pour conversion texte → path SVG avec largeur exacte
   * @private
   */
  _renderSecondary(formatResult, widthMm, heightMm, isSquare, arrowPosition, arrowOrientation, showArrow, forceCenter = false) {
    if (!this._titleText) {
      return;
    }

    const fontSizePx = this.mmToPx(formatResult.secondary.height);

    // Position sous le titre avec espacement
    const titleBottom = this._titleText.y() + this._titleText.offsetY();
    const spacingPx = this.mmToPx(formatResult.spacing);
    const secondaryY = titleBottom + spacingPx;

    // Buffer de sécurité entre texte et flèche
    const ARROW_BUFFER_MM = 10;

    // Configuration selon le format
    if (isSquare || forceCenter) {
      // Format carré : centrage parfait avec opentype.js
      const centerXPx = this.mmToPx(widthMm / 2);

      // Pour le format carré, wrapper manuellement le texte
      const MAX_CHARS_PER_LINE = 18;
      const words = formatResult.secondary.text.split(/\s+/);
      const wrappedLines = [];
      let currentLine = '';

      for (const word of words) {
        if (currentLine.length === 0) {
          currentLine = word;
        } else if ((currentLine + ' ' + word).length <= MAX_CHARS_PER_LINE) {
          currentLine += ' ' + word;
        } else {
          wrappedLines.push(currentLine);
          currentLine = word;
        }
      }
      if (currentLine.length > 0) {
        wrappedLines.push(currentLine);
      }

      // Calculer les dimensions
      const lineHeight = fontSizePx * 1.05; // Interligne réduit
      const totalHeight = wrappedLines.length * lineHeight;

      // Créer un groupe pour contenir toutes les lignes
      const secondaryGroup = new Konva.Group();

      wrappedLines.forEach((lineText, index) => {
        if (!lineText.trim()) return;

        const textResult = this._createTextPath(lineText, fontSizePx, this.COLORS.text);
        if (!textResult || !textResult.node) {
          console.error('[PanneauRenderer] Failed to create secondary text for:', lineText);
          return;
        }
        const lineNode = textResult.node;

        // Centrer cette ligne avec la largeur EXACTE
        const lineX = centerXPx - textResult.width / 2;
        const lineY = secondaryY + index * lineHeight;

        lineNode.x(lineX);
        lineNode.y(lineY);

        secondaryGroup.add(lineNode);
      });

      this.layer.add(secondaryGroup);
      this._secondaryGroup = secondaryGroup;

      // Recentrer verticalement le bloc titre + secondaire
      const titleTop = this._titleText.y();
      const secondaryBottom = secondaryY + totalHeight;
      const totalBlockHeight = secondaryBottom - titleTop;
      const centerYPx = this.mmToPx(heightMm / 2);
      const offsetY = centerYPx - (titleTop + totalBlockHeight / 2);

      // Décaler les groupes
      if (this._titleGroup) {
        this._titleGroup.y(this._titleGroup.y() + offsetY);
      }
      secondaryGroup.y(secondaryGroup.y() + offsetY);

      return;
    } else {
      // Format rectangle : alignement gauche ou droite selon position de la flèche
      const arrowHorizontalSpace = showArrow ? this._calculateArrowHorizontalSpace(arrowOrientation) : 0;
      const arrowTotalSpaceMm = arrowHorizontalSpace + ARROW_BUFFER_MM;

      const textZoneStartMm = arrowPosition === 'after' ? this.MARGIN_MM : (this.MARGIN_MM + arrowTotalSpaceMm);
      const textZoneEndMm = arrowPosition === 'after' ? (widthMm - this.MARGIN_MM - arrowTotalSpaceMm) : (widthMm - this.MARGIN_MM);

      // Créer le texte secondaire (une seule ligne pour le format rectangle)
      const textResult = this._createTextPath(formatResult.secondary.text, fontSizePx, this.COLORS.text);
      if (!textResult || !textResult.node) {
        console.error('[PanneauRenderer] Failed to create secondary text');
        return;
      }
      const secondaryNode = textResult.node;

      // Calculer la position X selon l'alignement
      let lineX;
      if (arrowPosition === 'after') {
        lineX = this.mmToPx(textZoneStartMm);
      } else {
        lineX = this.mmToPx(textZoneEndMm) - textResult.width;
      }

      secondaryNode.x(lineX);
      secondaryNode.y(secondaryY);

      this.layer.add(secondaryNode);

      // Recentrer verticalement le bloc titre+secondaire
      const titleTop = this._titleText.y();
      const secondaryBottom = secondaryY + textResult.height;
      const totalBlockHeight = secondaryBottom - titleTop;
      const centerYPx = this.mmToPx(heightMm / 2);
      const offsetY = centerYPx - (titleTop + totalBlockHeight / 2);

      // Décaler les groupes
      if (this._titleGroup) {
        this._titleGroup.y(this._titleGroup.y() + offsetY);
      }
      secondaryNode.y(secondaryNode.y() + offsetY);
    }
  }

  /**
   * Affiche le texte secondaire seul, centré (pour verso)
   * Utilise opentype.js pour conversion texte → path SVG avec largeur exacte
   * @private
   */
  _renderSecondaryAlone(formatResult, widthMm, heightMm) {
    const centerXPx = this.mmToPx(widthMm / 2);
    const centerYPx = this.mmToPx(heightMm / 2);
    const fontSizePx = this.mmToPx(formatResult.secondary.height);

    // Détecter si c'est le format carré (150x150mm)
    const isSquareFormat = (widthMm === 150 && heightMm === 150);

    // Wrapper le texte si nécessaire
    const MAX_CHARS_PER_LINE = isSquareFormat ? 18 : 40;
    const words = formatResult.secondary.text.split(/\s+/);
    const wrappedLines = [];
    let currentLine = '';

    for (const word of words) {
      if (currentLine.length === 0) {
        currentLine = word;
      } else if ((currentLine + ' ' + word).length <= MAX_CHARS_PER_LINE) {
        currentLine += ' ' + word;
      } else {
        wrappedLines.push(currentLine);
        currentLine = word;
      }
    }
    if (currentLine.length > 0) {
      wrappedLines.push(currentLine);
    }

    // Calculer les dimensions totales
    const lineHeight = fontSizePx * 1.05; // Interligne réduit
    const totalHeight = wrappedLines.length * lineHeight;
    const startY = centerYPx - totalHeight / 2;

    // Créer un groupe pour contenir toutes les lignes
    const versoGroup = new Konva.Group();

    wrappedLines.forEach((lineText, index) => {
      if (!lineText.trim()) return;

      const textResult = this._createTextPath(lineText, fontSizePx, this.COLORS.text);
      if (!textResult || !textResult.node) {
        console.error('[PanneauRenderer] Failed to create verso text for:', lineText);
        return;
      }
      const lineNode = textResult.node;

      // Centrer cette ligne avec la largeur EXACTE
      const lineX = centerXPx - textResult.width / 2;
      const lineY = startY + index * lineHeight;

      lineNode.x(lineX);
      lineNode.y(lineY);

      versoGroup.add(lineNode);
    });

    this.layer.add(versoGroup);
  }

  /**
   * Calcule l'espace horizontal occupé par la flèche selon sa rotation
   * @private
   */
  _calculateArrowHorizontalSpace(orientation) {
    const ARROW_WIDTH_MM = 28;
    const ARROW_HEIGHT_MM = 19.8;  // 70.152 / 99.216 * 28

    // Convertir l'orientation en radians
    const angleRad = (orientation * Math.PI) / 180;

    // Calculer la bounding box horizontale avec rotation
    // horizontal_space = |width * cos(θ)| + |height * sin(θ)|
    const horizontalSpace = Math.abs(ARROW_WIDTH_MM * Math.cos(angleRad)) +
                           Math.abs(ARROW_HEIGHT_MM * Math.sin(angleRad));

    return horizontalSpace;
  }

  /**
   * Affiche la flèche (format rectangulaire uniquement)
   * Utilise le SVG path réel MPDR avec proportions exactes
   * La flèche fait exactement 28mm de largeur sur les panneaux réels
   * @private
   */
  _renderArrow(widthMm, heightMm, position, orientation) {
    const centerY = this.mmToPx(heightMm / 2);

    // Dimensions réelles de la flèche sur les panneaux
    const ARROW_WIDTH_MM = 28;

    // Calculer l'espace horizontal réel occupé avec rotation
    const horizontalSpaceMm = this._calculateArrowHorizontalSpace(orientation);
    const halfHorizontalSpace = horizontalSpaceMm / 2;

    // Position de la flèche avec marge garantie de 20mm
    // On positionne le centre de la flèche de manière à ce que sa bounding box reste dans les marges
    const arrowX = position === 'after'
      ? this.mmToPx(widthMm - this.MARGIN_MM - halfHorizontalSpace)  // À droite
      : this.mmToPx(this.MARGIN_MM + halfHorizontalSpace);           // À gauche

    // SVG path réel MPDR (viewBox="0 0 99.21 70.15")
    // Path normalisé après transformation translate(-1239.91, -1668.59)
    const svgPath = 'M99.216,35.075 L64.139,0 L59.328,4.809 L86.193,31.674 L0,31.674 L0,38.477 L86.193,38.477 L59.328,65.342 L64.139,70.152 z';

    // Dimensions du SVG viewBox
    const SVG_WIDTH = 99.216;
    const SVG_HEIGHT = 70.152;

    // Calculer le scale pour que la flèche fasse exactement 28mm de large
    // Scale = largeur_cible_px / largeur_svg
    const arrowScale = this.mmToPx(ARROW_WIDTH_MM) / SVG_WIDTH;

    // IMPORTANT - Compensation du miroir horizontal pour position="before"
    // ============================================================================
    // Quand position="before", on utilise scaleX négatif pour créer un miroir.
    // Ce miroir inverse visuellement les directions (↗ devient ↖, etc.).
    //
    // Pour que la flèche affiche la bonne direction malgré le miroir :
    // - On applique la formule : effectiveRotation = orientation - 180
    //
    // Exemple : Pour afficher ↗ (value=-45 dans le template)
    // - Sans miroir (after) : rotation = -45 → affiche ↗ ✓
    // - Avec miroir (before) : rotation = -45 - 180 = -225 = 135° → après miroir affiche ↗ ✓
    //
    // NE PAS MODIFIER cette formule sans tester toutes les 8 directions
    // dans les deux positions (before et after).
    // ============================================================================
    const effectiveRotation = position === 'before' ? (orientation - 180) : orientation;

    // Créer le path Konva avec le SVG réel
    const arrow = new Konva.Path({
      x: arrowX,
      y: centerY,
      data: svgPath,
      fill: this.COLORS.text,  // La flèche MPDR est remplie (fill), pas en stroke
      // Scaling pour respecter les 28mm de largeur réelle
      scaleX: position === 'after' ? arrowScale : -arrowScale,  // Miroir si position before
      scaleY: arrowScale,
      offsetX: SVG_WIDTH / 2,   // Centrer horizontalement sur le path
      offsetY: SVG_HEIGHT / 2,  // Centrer verticalement sur le path
      rotation: effectiveRotation     // Appliquer la rotation (inversée si before)
    });

    this.layer.add(arrow);
  }

  /**
   * Affiche le SVG de fond pour le verso (rectangulaire ou carré)
   * @private
   */
  _renderBackgroundSVG(widthMm, heightMm, isSquare) {
    // Charger le bon SVG selon le format
    const svgUrl = isSquare
      ? window.PANNEAU_SVG_URLS?.carre
      : window.PANNEAU_SVG_URLS?.rectangle;

    if (!svgUrl) {
      console.warn('[Renderer] URL du SVG de fond non définie pour format:', isSquare ? 'carré' : 'rectangle');
      return;
    }

    // Créer une image HTML
    const imageObj = new Image();
    imageObj.crossOrigin = 'Anonymous'; // Pour éviter les problèmes CORS

    // Ajouter un timeout pour éviter un blocage indéfini
    const timeoutId = setTimeout(() => {
      console.error('[Renderer] Timeout du chargement SVG (5s)');
      imageObj.src = ''; // Annuler le chargement
    }, this.SVG_LOAD_TIMEOUT_MS);

    imageObj.onload = () => {
      clearTimeout(timeoutId);
      // Dimensions originales du SVG viewBox selon le format
      let svgOriginalWidth, svgOriginalHeight;
      if (isSquare) {
        // Format carré: viewBox 425.2 x 425.2
        svgOriginalWidth = 425.2;
        svgOriginalHeight = 425.2;
      } else {
        // Format rectangle: viewBox 850.4 x 283.5
        svgOriginalWidth = 850.4;
        svgOriginalHeight = 283.5;
      }
      const svgRatio = svgOriginalHeight / svgOriginalWidth;

      // Calculer la largeur cible en mm (toute la largeur du panneau)
      const targetWidthMm = widthMm;
      const targetHeightMm = targetWidthMm * svgRatio;

      // Convertir en pixels
      const targetWidthPx = this.mmToPx(targetWidthMm);
      const targetHeightPx = this.mmToPx(targetHeightMm);

      // Calculer la position pour centrer le SVG
      const centerX = this.mmToPx(widthMm / 2);
      const centerY = this.mmToPx(heightMm / 2);

      // Créer l'image Konva
      const backgroundSvg = new Konva.Image({
        x: centerX,
        y: centerY,
        image: imageObj,
        width: targetWidthPx,
        height: targetHeightPx,
        offsetX: targetWidthPx / 2,  // Centrer horizontalement
        offsetY: targetHeightPx / 2,  // Centrer verticalement
        opacity: 1
      });

      // Ajouter au layer (juste après le fond et les marges, avant le texte)
      // On doit l'insérer à l'index 2 (après background et marginRect)
      this.layer.add(backgroundSvg);
      backgroundSvg.moveToBottom(); // S'assurer qu'il est derrière
      backgroundSvg.moveUp(); // Mais au-dessus du fond
      backgroundSvg.moveUp(); // Et au-dessus des marges

      this.layer.batchDraw();
    };

    imageObj.onerror = (err) => {
      clearTimeout(timeoutId);
      console.error('[Renderer] Erreur lors du chargement du SVG de fond:', err);
      console.error('[Renderer] URL qui a échoué:', svgUrl);
    };

    imageObj.src = svgUrl;
  }

  /**
   * Détruit le renderer et libère les ressources
   */
  destroy() {
    if (this.stage) {
      this.stage.destroy();
      this.stage = null;
      this.layer = null;
    }
  }

  /**
   * Export du panneau en image PNG
   * @returns {string} Data URL de l'image
   */
  toDataURL() {
    if (!this.stage) return null;
    return this.stage.toDataURL();
  }
}

// Export pour utilisation dans Shopify
window.PanneauRenderer = PanneauRenderer;
